import { ref, computed, watch, type Ref } from 'vue'
import { useRpc, useRpcCall } from '#storefront/composables'

/** Generate a UUID v4; works in non-secure contexts and older browsers where crypto.randomUUID is missing. */
function getVisitorId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  // Fallback: RFC4122 UUIDv4 using getRandomValues (broader support) or Math.random
  const bytes = new Uint8Array(16)
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    crypto.getRandomValues(bytes)
  } else {
    // Fallback only used in legacy contexts without crypto.getRandomValues;
    // visitor ID is for view-count analytics, not a security boundary.
    for (let i = 0; i < bytes.length; i++) {
      // eslint-disable-next-line sonarjs/pseudo-random
      bytes[i] = (Math.random() * 256) | 0
    }
  }

  // Set version (4) and variant (10xx)
  bytes[6] = ((bytes[6] as number) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
}

const useProductViewCounter = (productId: Ref<string | number>) => {
  const { user, isLoggedIn } = useAgsUser()
  const fetching = ref(false)
  const error = ref<any>(null)

  // Make key reactive so cache updates when product changes
  const key = computed(() => `product-view-counter-${productId.value}`)

  const {
    data,
    refresh,
    error: countError,
    status: countStatus,
  } = useRpc('getProductViewCount', key.value, () => ({
    productId: productId.value.toString(),
  }))

  const recordProductView = async () => {
    if (import.meta.server) {
      return
    }

    if (!productId.value) {
      return
    }

    // Determine visitor ID
    let visitorId: string
    if (isLoggedIn && user.value?.id) {
      visitorId = `user-${user.value.id}`
    } else {
      visitorId = localStorage.getItem('visitorId') || getVisitorId()
      localStorage.setItem('visitorId', visitorId)
    }

    fetching.value = true
    error.value = null

    try {
      const call = useRpcCall('recordProductView')

      await call({
        productId: productId.value.toString(),
        visitorId,
      })

      // Refresh count after recording a view
      await refresh()
    } catch (err) {
      console.error('Error recording product view:', err)
      error.value = err
    } finally {
      fetching.value = false
    }
  }

  // 🔥 React to productId changes (e.g. color switch)
  watch(
    productId,
    (newId, oldId) => {
      if (!newId || newId === oldId) {
        return
      }
      recordProductView()
    },
    { immediate: true },
  )

  return {
    key,
    data,
    refresh,
    countError,
    countStatus,
    recordProductView,
    fetching,
    error,
  }
}

export default useProductViewCounter
